import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { useState } from 'react';
import { MKT } from '../../Marketing/links';
// Example defaults: Japan-side lines from one real five-car kei shipment (Aug 2026, as in the
// importing guide); the US-side lines are placeholders to replace with your own quotes.
const DEFAULTS = {
    hammer: 400000,
    fx: 160,
    agent: 100000,
    auctionFee: 20000,
    inland: 20000,
    insurance: 10000,
    freight: 170000,
    dutyPct: 2.5,
    broker: 300,
    port: 600,
    title: 250,
    recon: 500,
    marginPct: 20
};
// Merchandise Processing Fee: 0.3464% of entered value between a floor and a cap CBP resets every
// October 1 (these are FY2027's, from Oct 1, 2026). Harbor Maintenance Fee: 0.125% by sea.
const MPF_RATE = 0.003464;
const MPF_MIN = 34.58;
const MPF_MAX = 670.86;
const HMF_RATE = 0.00125;
export function landedCost(v, basis) {
    const japanYen = v.hammer + v.agent + v.auctionFee + v.inland + v.insurance + v.freight;
    const japanUsd = v.fx > 0 ? japanYen / v.fx : 0;
    // Entered value: what the broker declares. Some entries use the vehicle price alone, others
    // the invoice total before freight; ask your broker which applies.
    const enteredYen = basis === 'hammer' ? v.hammer : v.hammer + v.agent + v.auctionFee + v.inland;
    const entered = v.fx > 0 ? enteredYen / v.fx : 0;
    const duty = entered * v.dutyPct / 100;
    const mpf = entered > 0 ? Math.min(MPF_MAX, Math.max(MPF_MIN, entered * MPF_RATE)) : 0;
    const hmf = entered * HMF_RATE;
    const usSide = duty + mpf + hmf + v.broker + v.port + v.title + v.recon;
    const total = japanUsd + usSide;
    const sale = v.marginPct < 100 ? total / (1 - v.marginPct / 100) : 0;
    return {
        japanYen,
        japanUsd,
        entered,
        duty,
        mpf,
        hmf,
        total,
        sale
    };
}
const usd = (n)=>n.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
    });
const yen = (n)=>`¥${Math.round(n).toLocaleString('en-US')}`;
const FIELDS = [
    {
        key: 'hammer',
        label: 'Hammer price',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'fx',
        label: 'Exchange rate (yen per dollar)',
        unit: '¥/$',
        group: 'jp'
    },
    {
        key: 'agent',
        label: 'Agent service fee',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'auctionFee',
        label: 'Auction fee',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'inland',
        label: 'Inland transport to port',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'insurance',
        label: 'Insurance',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'freight',
        label: 'RoRo freight',
        unit: '¥',
        group: 'jp'
    },
    {
        key: 'dutyPct',
        label: 'Duty rate (from your broker)',
        unit: '%',
        group: 'us'
    },
    {
        key: 'broker',
        label: 'Customs broker',
        unit: '$',
        group: 'us'
    },
    {
        key: 'port',
        label: 'Port, terminal and trucking',
        unit: '$',
        group: 'us'
    },
    {
        key: 'title',
        label: 'Title and registration',
        unit: '$',
        group: 'us'
    },
    {
        key: 'recon',
        label: 'Reconditioning',
        unit: '$',
        group: 'us'
    },
    {
        key: 'marginPct',
        label: 'Target margin on sale price',
        unit: '%',
        group: 'sale'
    }
];
const GROUPS = [
    [
        'jp',
        'Japan side (your agent’s invoice)'
    ],
    [
        'us',
        'US side'
    ],
    [
        'sale',
        'Selling'
    ]
];
function Calculator() {
    const [v, setV] = useState(DEFAULTS);
    const [basis, setBasis] = useState('hammer');
    const r = landedCost(v, basis);
    const set = (key)=>(e)=>{
            const n = Number(e.target.value);
            setV((cur)=>({
                    ...cur,
                    [key]: Number.isFinite(n) && n >= 0 ? n : 0
                }));
        };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mk-calc",
        "data-testid": Testids.app.blog.calculator,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mk-calc__inputs",
                children: GROUPS.map(([g, title])=>/*#__PURE__*/ _jsxs("fieldset", {
                        className: "mk-calc__group",
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                children: title
                            }),
                            FIELDS.filter((f)=>f.group === g).map((f)=>/*#__PURE__*/ _jsxs("label", {
                                    className: "mk-calc__field",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: f.label
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "mk-calc__input",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "mk-calc__unit",
                                                    children: f.unit
                                                }),
                                                /*#__PURE__*/ _jsx("input", {
                                                    type: "number",
                                                    inputMode: "decimal",
                                                    min: 0,
                                                    step: f.unit === '%' || f.key === 'fx' ? 0.1 : 1,
                                                    value: v[f.key],
                                                    onChange: set(f.key),
                                                    "data-testid": Testids.app.blog.calcInput(f.key)
                                                })
                                            ]
                                        })
                                    ]
                                }, f.key)),
                            g === 'us' && /*#__PURE__*/ _jsxs("fieldset", {
                                className: "mk-calc__field mk-calc__field--set",
                                children: [
                                    /*#__PURE__*/ _jsx("legend", {
                                        children: "Duty is charged on"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "mk-calc__basis",
                                        children: [
                                            [
                                                'hammer',
                                                'Hammer price'
                                            ],
                                            [
                                                'fob',
                                                'Hammer + Japan fees'
                                            ]
                                        ].map(([b, label])=>/*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                "aria-pressed": basis === b,
                                                className: basis === b ? 'is-on' : undefined,
                                                onClick: ()=>setBasis(b),
                                                children: label
                                            }, b))
                                    })
                                ]
                            })
                        ]
                    }, g))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mk-calc__result",
                "aria-live": "polite",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Japan side"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    yen(r.japanYen),
                                    " = ",
                                    usd(r.japanUsd)
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Entered value"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: usd(r.entered)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    "Duty (",
                                    v.dutyPct,
                                    "%)"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: usd(r.duty)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "MPF"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: usd(r.mpf)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "HMF"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: usd(r.hmf)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Broker, port, title, recon"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: usd(v.broker + v.port + v.title + v.recon)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line mk-calc__line--total",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Landed cost"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.app.blog.calcTotal,
                                children: usd(r.total)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-calc__line mk-calc__line--sale",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    "Sell at for a ",
                                    v.marginPct,
                                    "% margin"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": Testids.app.blog.calcSale,
                                children: usd(r.sale)
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export default function CostCalculator() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "The hammer price is where a car’s cost starts, not where it ends. By the time a car from a Japanese auction is titled in the US, agent and auction fees, inland transport, freight, duty, customs fees, the broker, the port and reconditioning have often added as much again. This JDM import cost calculator adds it all up so you can bid with the landed cost, and the sale price, in front of you."
            }),
            /*#__PURE__*/ _jsx(Calculator, {}),
            /*#__PURE__*/ _jsx("blockquote", {
                children: "Every default here is an example. The Japan-side numbers come from one real kei shipment in August 2026; the US-side ones are placeholders. Replace them with your agent’s invoice and your broker’s quote. Duty rates on Japanese vehicles have changed several times since 2025, so use the rate your broker gives you."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The Japan side"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Your export agent invoices in yen: the hammer price, their service fee, the auction’s fee, inland transport to the port, insurance, and freight. RoRo (roll-on, roll-off) freight is priced by the vehicle’s size, so a kei car costs far less to ship than a Land Cruiser. The exchange rate on each invoice can move between invoices, so record it per invoice. See",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/choosing-a-japanese-export-agent",
                        children: "choosing a Japanese export agent"
                    }),
                    " for what to ask about fees."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Duty, MPF and HMF"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Your customs broker files the entry (CBP form 7501) and declares the entered value. Duty is a percentage of that value at the rate for how the vehicle is classified: passenger cars carried 2.5% for many years and trucks 25%, and tariffs on Japanese vehicles have changed since 2025. The Merchandise Processing Fee is 0.3464% of entered value, with a minimum of $34.58 and a maximum of $670.86 for entries from October 1, 2026 (CBP resets them each October), and the Harbor Maintenance Fee is 0.125% for cargo arriving by sea."
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Whether the entered value is the vehicle price alone or the invoice with the Japan-side fees depends on the entry; on one recent entry it was the vehicle price alone. The toggle above shows the difference. Ask your broker which applies."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Port, title and reconditioning"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "At the port you’ll pay terminal and handling charges and trucking to your shop, then title and registration in your state, and whatever the car needs before sale. On a kei truck these fixed costs can be a big share of the total; on a Supra, a small one."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Pricing the sale"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "The last line turns the landed cost into the price you need for a target margin on the sale price: landed cost ÷ (1 − margin). Work back from it at bid time: if the sale price a car needs is above what similar cars sell for, lower your maximum bid."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Track the real numbers"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "An estimate gets you a bid. Right Hand DMS keeps the real numbers: every line from your agent’s invoices, the 7501, the broker and the port is read off the paperwork and added to the car, with each invoice’s exchange rate, so you see each car’s real landed cost and profit when it sells. See ",
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.product('landed-cost'),
                        children: "landed cost"
                    }),
                    " and the",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.guide,
                        children: "importing guide"
                    }),
                    "."
                ]
            })
        ]
    });
}
